<template>
  <CollapsibleRoot
    v-model:open="isOpen"
    class="dp-root"
    :class="{ [`is-${statusName}`]: statusName }"
  >
    <CollapsibleTrigger
      v-if="isCollapsible"
      as="header"
      class="dp-header"
      :style="headerStyles"
    >
      <span v-if="icon" class="icon dp-icon mr-1">
        <i :class="normalizedIcon" />
      </span>

      <div class="dp-title is-size-6">
        <slot name="header" />
        <span
          v-if="count != null"
          class="tag tag-count is-rounded"
          :class="countBadgeClass"
        >
          {{ count }}
        </span>
      </div>

      <div class="dp-header-right" @click.stop>
        <slot name="header-controls" />
      </div>

      <span v-if="containerState.refresh" class="dp-refresh-btn" @click.stop>
        <IconButton
          icon="fa-solid fa-rotate"
          :loading="containerState.isRefreshing"
          :disabled="containerState.refreshDisabled"
          :tooltip="containerState.refreshTooltip || 'Refresh'"
          size="small"
          @click="containerState.refresh()"
        />
      </span>

      <span
        v-if="statusName"
        v-tooltip="statusTooltip"
        class="dp-status-badge"
        @click.stop
      >
        <i :class="statusBadgeIcon" />
      </span>

      <span v-if="$slots.help" class="dp-help" @click.stop>
        <slot name="help" />
      </span>

      <span
        v-tooltip="!hasContent ? 'Panel is empty' : undefined"
        class="dp-chevron"
        :class="{ 'dp-chevron--empty': !hasContent }"
      >
        <i v-if="isLoading && !isOpen" class="fa-solid fa-spinner fa-spin" />
        <i
          v-else
          class="fa-solid"
          :class="isOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
        />
      </span>
    </CollapsibleTrigger>
    <header v-else class="dp-header" :style="headerStyles">
      <span v-if="icon" class="icon dp-icon mr-1">
        <i :class="normalizedIcon" />
      </span>

      <div class="dp-title is-size-6 is-static">
        <slot name="header" />
        <span
          v-if="count != null"
          class="tag tag-count is-rounded"
          :class="countBadgeClass"
        >
          {{ count }}
        </span>
      </div>

      <div class="dp-header-right">
        <slot name="header-controls" />
      </div>

      <span v-if="containerState.refresh" class="dp-refresh-btn" @click.stop>
        <IconButton
          icon="fa-solid fa-rotate"
          :loading="containerState.isRefreshing"
          :disabled="containerState.refreshDisabled"
          :tooltip="containerState.refreshTooltip || 'Refresh'"
          size="small"
          @click="containerState.refresh()"
        />
      </span>
    </header>

    <div v-if="$slots.toolbar" class="dp-toolbar">
      <slot name="toolbar" />
    </div>

    <div v-if="isLoading && isOpen" class="dp-progress">
      <div class="dp-progress__bar" />
    </div>

    <CollapsibleContent class="dp-body">
      <slot />
    </CollapsibleContent>
  </CollapsibleRoot>
</template>

<script>
import {
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
} from 'reka-ui'

export default {
  name: 'DefaultPanel',
  components: { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent },
  provide() {
    return { containerState: this.containerState }
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
    status: {
      type: [String, Object],
      default: null,
    },
    icon: {
      type: [String, Array],
      default: null,
    },
    count: {
      type: Number,
      default: null,
    },
    isCollapsible: {
      type: Boolean,
      default: true,
    },
    openWhenEmpty: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    headerStyles: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['open', 'close', 'toggle'],
  data() {
    return {
      isOpen: !this.collapsed,
      containerState: {
        loading: false,
        isRefreshing: false,
        refresh: null,
        refreshDisabled: false,
        refreshTooltip: 'Refresh',
      },
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.containerState.loading
    },
    statusName() {
      if (!this.status) return null
      return typeof this.status === 'string' ? this.status : this.status.name
    },
    statusBadgeIcon() {
      const icons = {
        warning: 'fa-solid fa-exclamation-triangle has-text-warning',
        danger: 'fa-solid fa-exclamation-circle has-text-danger',
        success: 'fa-solid fa-check-circle has-text-success',
        info: 'fa-solid fa-info-circle has-text-info',
      }
      return icons[this.statusName] || null
    },
    statusTooltip() {
      if (typeof this.status === 'object' && this.status?.message) {
        return this.status.message
      }
      const labels = {
        warning: 'Warning',
        danger: 'Error',
        success: 'OK',
        info: 'Info',
      }
      return labels[this.statusName] || null
    },
    countBadgeClass() {
      if (this.statusName) return `is-${this.statusName}`
      return this.count > 0 ? 'is-primary' : ''
    },
    normalizedIcon() {
      if (!this.icon) return null
      return Array.isArray(this.icon) ? this.icon : this.icon.split(' ')
    },
    hasContent() {
      return this.count !== 0
    },
  },
  mounted() {
    if (this.isCollapsible && !this.hasContent && !this.openWhenEmpty)
      this.isOpen = false
  },
  watch: {
    isOpen(open) {
      this.$emit(open ? 'open' : 'close')
      this.$emit('toggle', open)
    },
    hasContent(val) {
      if (this.isCollapsible && !val && !this.openWhenEmpty) this.isOpen = false
    },
    collapsed(val) {
      this.isOpen = !val
    },
  },
}
</script>

<style lang="scss" scoped>
$radius: var(--radius-lg);

.dp-root {
  border: 1px solid var(--color-border-default);
  border-radius: $radius;
  background: var(--color-background-surface, #fff);
  overflow: hidden;
}

/* ── Header ─────────────────────────────────────────────── */
.dp-header {
  display: flex;
  align-items: center;
  font-size: var(--text-base);
  padding: var(--panel-padding-y) var(--panel-padding-x);
  background-color: var(--color-background-default);
  border-bottom: 1px solid var(--color-border-default);
  color: var(--color-text-default, inherit);
  gap: var(--space-2);
  cursor: default;

  &[data-state] {
    cursor: pointer;
  }

  /* No border when toolbar follows (toolbar owns the separator) */
  &:has(+ .dp-toolbar) {
    border-bottom: none;
  }

  /* No border when closed or open with empty body */
  [data-state='closed'] > &,
  .dp-root:not(:has(.dp-body > *)) > & {
    border-bottom: none;
  }
}

/* ── Toolbar (always visible, between header and content) ─ */
.dp-toolbar {
  padding: 0 var(--panel-padding-x) var(--panel-padding-y);
  background-color: var(--color-background-default);
  border-bottom: 1px solid var(--color-border-default);

  [data-state='closed'] > &,
  .dp-root:not(:has(.dp-body > *)) > & {
    border-bottom: none;
  }
}

.dp-icon {
  color: var(--text-muted);
  flex-shrink: 0;
}

.dp-status-badge {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 0.8rem;
}

.dp-title {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  font-weight: var(--font-semibold);
  gap: var(--space-2);
}

.dp-header-right {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex: 0 0 auto;
  justify-content: flex-end;
  min-width: 0;
  container-type: inline-size;

  &:empty {
    display: none;
  }
}

.dp-help {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--text-muted);
  cursor: help;
}

.dp-refresh-btn {
  display: flex;
  align-items: center;
  flex-shrink: 0;

  :deep(.icon-btn) {
    color: var(--text-subtle);
    transition: color var(--transition-fast);

    &:hover {
      color: var(--text-muted);
    }
  }
}

.dp-chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--space-6);
  height: var(--space-6);
  border-radius: 50%;
  color: var(--color-text-muted, inherit);
  font-size: 0.7rem;
  flex-shrink: 0;

  &.is-disabled {
    opacity: 0.3;
  }

  &.dp-chevron--empty {
    opacity: 0.3;
  }
}

/* ── Progress bar ──────────────────────────────────────── */
.dp-progress {
  height: 3px;
  background-color: var(--border-default);
  overflow: hidden;
}

.dp-progress__bar {
  height: 100%;
  width: 40%;
  background-color: var(--primary);
  border-radius: 2px;
  animation: dp-progress-slide 1.2s ease-in-out infinite;
}

@keyframes dp-progress-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}

/* ── Content ────────────────────────────────────────────── */
.dp-body {
  overflow: hidden;

  > :deep(:not(.edge-to-edge)) {
    padding: var(--panel-padding-y) var(--panel-padding-x);
  }

  // Fade in content when it appears after async load
  > :deep(*) {
    animation: dp-fade-in 0.25s ease-out;
  }
}

/* ── Injected list height constraint ───────────────────── */
.dp-body:has(.injected-list) {
  :deep(.injected-list) {
    max-height: calc(80vh - 3rem);
  }
}

/* ── Collapse / expand animation ───────────────────────── */
.dp-body[data-state='open'] {
  animation: dp-expand 0.2s ease-out;
}

.dp-body[data-state='closed'] {
  animation: dp-collapse 0.2s ease-out;
}

@keyframes dp-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes dp-expand {
  from {
    height: 0;
    opacity: 0;
  }
  to {
    height: var(--reka-collapsible-content-height);
    opacity: 1;
  }
}

@keyframes dp-collapse {
  from {
    height: var(--reka-collapsible-content-height);
    opacity: 1;
  }
  to {
    height: 0;
    opacity: 0;
  }
}
</style>
